import { useElementSize } from "@mantine/hooks";
import { useEffect }      from "react";

import { useGetProStatus, useGetProfileData } from "_common/hooks/auth";
import { useGetDeviceType }                   from "_common/hooks/global";
import useProContent                          from "_common/hooks/useProContent";
import CardWrapper                            from "_components/atoms/cardWrapper";
import Render                                 from "_components/atoms/render";
import { useRTCWebSocketContext }             from "_components/features/rtcSocket";
import BroadcasterHeader                      from "_components/molecules/broadcasterHeader";
import LoginWall                              from "_components/molecules/loginWall";
import { STREAM_TIER }                        from "_components/pages/proPage/constants";
import RewardedAds                            from "_components/templates/rewardedAds";
import {
	useGetAdWatchStatus,
	useSetAdWatchStatus,
	useSetCanWatchMoreProContent
} from "_jotai/rooterPro";

import SessionDetails from "./components/sessionDetails";
import Media          from "./media";
import useStyles      from "./styles";

const component = props => {
	const {
		isStreamer,
		broadcastData,
		sessionData,
		broadcastId,
		handleTheatreMode,
		theatreModeToggle,
		isBlockedByStreamer,
		totalReactions,
		staticTotalComments,
		staticTotalReactions
	} = props;

	const { classes }               = useStyles ( undefined, { name: "styles" } );
	const deviceType                = useGetDeviceType ();
	const isMobile                  = deviceType === "mobile";
	const setCanWatchMoreProContent = useSetCanWatchMoreProContent ();
	const { sendMessage }           = useRTCWebSocketContext ();
	const setAdWatchStatus          = useSetAdWatchStatus ();
	const adWatchStatus             = useGetAdWatchStatus ();

	let id, photo, name, isCeleb, isFollowing;

	if ( broadcastData && broadcastData.broadcaster && broadcastData.broadcaster.sportsFan ) {
		id          = broadcastData.broadcaster.sportsFan.id;
		photo       = broadcastData.broadcaster.sportsFan.photo;
		name        = broadcastData.broadcaster.sportsFan.name;
		isCeleb     = broadcastData.broadcaster.sportsFan.isCeleb;
		isFollowing = broadcastData.broadcaster.sportsFan.isFollowing;
	}

	const theatreModeStyles = theatreModeToggle ? {
		background: "black", color: "#fff", height: "inherit", flex: "1", border: "none", borderRadius: "0", padding: 0
	} : { boxShadow: "none", border: "none", width: "100%", borderRadius: "10px !important" };

	let theatreModeHeight = theatreModeToggle && "calc(100vh - 110px)";

	useEffect ( () => {
		return () => {
			setAdWatchStatus ( {
				opened   : false,
				finished : false,
				skipped  : false
			} );
		};
	}, [] );

	const onAdFinish = () => {

		sendMessage ( {
			event : "lsAdView",
			data  : {
				sessionId: broadcastId
			}
		} );

		setCanWatchMoreProContent ( true );
		setAdWatchStatus ( { skipped: false, opened: false, finished: true } );
	};
	const { ref, height } = useElementSize ();

	const description    = sessionData?.description || broadcastData?.description;
	const followersCount = broadcastData.broadcaster?.followerCount;

	const header = theatreModeToggle && (
		<BroadcasterHeader
			description       = { description }
			followersCount    = { followersCount }
			game              = { broadcastData?.game }
			id                = { id }
			isCeleb           = { isCeleb }
			isFollowingUser   = { isFollowing }
			name              = { name }
			photo             = { photo }
			sessionDataId     = { broadcastData?.id }
			sessionType       = { broadcastData?.sessionType }
			streamTitle       = { broadcastData?.sessionInfo }
			theatreModeToggle = { theatreModeToggle }
		/>
	);

	const userProfile = useGetProfileData ();
	const isOwnStream = userProfile?.id === broadcastData?.broadcaster?.sportsFan?.id;
	const isProUser   = useGetProStatus ();
	const streamTier  = broadcastData?.streamTier?.toString ();
	const isProStream = [ STREAM_TIER.PRO_STREAM, STREAM_TIER.MID_STREAM ].includes ( streamTier );

	const hasEnded = !!broadcastData.endTimeUTC;

	const proDetails = useProContent ( {
		proUser                : isProUser || isOwnStream,
		isProStream            : isProStream,
		remainingAdCount       : broadcastData.midTierRemainingAdWatchCount,
		remainingWatchDuration : broadcastData?.remainingWatchDuration,
		streamTier             : broadcastData.streamTier,
		isEndedStream          : hasEnded,
		broadcastId            : broadcastData.id
	} );

	const player = (
		<div
			ref       = { ref }
			className = { classes.playerContainer }
			style     = { {
				height         : theatreModeToggle ? theatreModeHeight : "100%",
				width          : theatreModeToggle ? height * ( 16 / 9 ) : "100%",
				maxWidth       : theatreModeToggle ? "76vw" : "100%",
				position       : "relative",
				justifyContent : "center",
				margin         : "auto"
			} }
		>
			<div className = { theatreModeToggle ?
				classes.darkModeNoBorderRadiusPlayer :
				classes.darkModeBorderBroadcastPlayer }
			>

				<Render condition = { adWatchStatus.opened }>
					<RewardedAds onAdFinish = { onAdFinish } />
				</Render>

				<LoginWall
					adExtConfig                  = { broadcastData?.adExtnDetail }
					background                   = { broadcastData.thumbnail }
					broadcasterId                = { broadcastData.broadcaster?.sportsFan?.id }
					isProStream                  = { isProStream }
					isReel                       = { false }
					streamTier                   = { broadcastData.streamTier }
				>
					<Media
						broadcastData       = { broadcastData }
						handleTheatreMode   = { handleTheatreMode }
						isBlockedByStreamer = { isBlockedByStreamer }
						isProStream         = { isProStream }
						isStreamer          = { isStreamer }
						proDetails          = { proDetails }
						sessionData         = { sessionData }
						streamTier          = { broadcastData.streamTier }
						theatreModeToggle   = { theatreModeToggle }
					/>
				</LoginWall>
			</div>
		</div>
	);

	if ( isMobile ) {
		return (
			<>
				{player}

				<SessionDetails
					broadcastData        = { broadcastData }
					broadcastId          = { broadcastId }
					isBlockedByStreamer  = { isBlockedByStreamer }
					sessionData          = { sessionData }
					staticTotalComments  = { staticTotalComments }
					staticTotalReactions = { staticTotalReactions }
					theatreModeToggle    = { theatreModeToggle }
					totalReactions       = { totalReactions }
				/>
			</>

		);
	}

	return (
		<CardWrapper
			noBackground
			customStyles = { theatreModeStyles }
			type         = { "secondary" }
		>
			{header}

			{player}

			<SessionDetails
				broadcastData        = { broadcastData }
				broadcastId          = { broadcastId }
				isBlockedByStreamer  = { isBlockedByStreamer }
				sessionData          = { sessionData }
				staticTotalComments  = { staticTotalComments }
				staticTotalReactions = { staticTotalReactions }
				theatreModeToggle    = { theatreModeToggle }
				totalReactions       = { totalReactions }
			/>
		</CardWrapper>
	);
};

export default component;
